import type { VerwaltungPartner } from '@nac2s/shared'
import { useEffect, useState } from 'react'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { ladePartner, ladePlattform } from '../api/verwaltung.js'
import { Seitenkopf } from '../layout/AppShell.js'

/**
 * Proxy-Übersicht (ADR-0008, ADR-0015): Welche Hosts der Rand kennen müsste
 * und wohin sie führen — zentral, aus den Partnerdaten erzeugt. Heute nur
 * zum Ansehen: Der Rand liest diese Konfiguration noch nicht, auf Staging
 * steht sie fest in `deploy/proxy/Caddyfile.staging`.
 *
 * Woher der Rand einen fremden Stack erreicht, weiss die Verwaltung noch
 * nicht — dafür fehlt ein Feld am Partner. Deshalb stehen dort Platzhalter.
 */

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; partner: VerwaltungPartner[]; plattform: string | null }
  | { status: 'fehler'; meldung: string }

interface Route {
  host: string
  ziel: string
  zweck: string
  partner: string
  hinweis: string | null
}

function routenVon(partner: VerwaltungPartner[], plattform: string | null): Route[] {
  const routen: Route[] = []
  for (const p of partner) {
    if (p.zustand === 'gekuendigt') continue
    const dienst = (name: string) =>
      p.stackEndpoint && !new URL(p.stackEndpoint).hostname.includes('.')
        ? `${name}:8080`
        : `<Rand-Adresse des Stacks von ${p.name}>:${name === 'web' ? 443 : 443}`
    const hinweis =
      p.stackEndpoint && !new URL(p.stackEndpoint).hostname.includes('.')
        ? null
        : 'Adresse des Stacks für den Rand ist noch nicht hinterlegt.'
    if (p.subdomain && plattform) {
      routen.push({
        host: `${p.subdomain}.${plattform}`,
        ziel: dienst('partner-web'),
        zweck: 'Kundenverwaltung',
        partner: p.name,
        hinweis,
      })
      routen.push({
        host: `*.${p.subdomain}.${plattform}`,
        ziel: dienst('web'),
        zweck: 'Kunden (Kundenanwendung)',
        partner: p.name,
        hinweis,
      })
    }
    if (p.whiteLabel && p.baseDomain) {
      routen.push({
        host: `partner.${p.baseDomain}`,
        ziel: dienst('partner-web'),
        zweck: 'Kundenverwaltung (White-Label)',
        partner: p.name,
        hinweis: p.domainVerifiedAt
          ? hinweis
          : 'Domain noch nicht nachgewiesen (Projektstruktur.md §6.2).',
      })
      routen.push({
        host: `*.${p.baseDomain}`,
        ziel: dienst('web'),
        zweck: 'Kunden (White-Label)',
        partner: p.name,
        hinweis: p.domainVerifiedAt
          ? hinweis
          : 'Domain noch nicht nachgewiesen (Projektstruktur.md §6.2).',
      })
    }
    if (!p.subdomain && !(p.whiteLabel && p.baseDomain)) {
      routen.push({
        host: '—',
        ziel: '—',
        zweck: 'keine Domain',
        partner: p.name,
        hinweis:
          'Weder Subdomain noch White-Label-Domain — der Partner ist über den Rand nicht erreichbar.',
      })
    }
  }
  return routen
}

function caddyfileVon(routen: Route[], plattform: string | null): string {
  const zeilen: string[] = [
    '# Erzeugt aus der Verwaltung (ADR-0008) — Vorschau, nicht ausgerollt.',
    '# Zertifikate: Wildcard je Partner braucht DNS-01; White-Label CNAME plus On-Demand',
    '# nur für nachgewiesene Domains (Projektstruktur.md §6.2, ST-034).',
    '',
    '(sicherheit) {',
    '\ttls {',
    '\t\tprotocols tls1.2 tls1.3',
    '\t}',
    '\theader {',
    '\t\tStrict-Transport-Security "max-age=300"',
    '\t\tX-Content-Type-Options nosniff',
    '\t\t-Server',
    '\t}',
    '}',
    '',
  ]
  if (plattform) {
    zeilen.push(
      `verwaltung.${plattform} {`,
      '\timport sicherheit',
      '\treverse_proxy admin-web:8080',
      '}',
      '',
    )
  }
  for (const r of routen) {
    if (r.host === '—') continue
    zeilen.push(
      `# ${r.partner} — ${r.zweck}`,
      `${r.host} {`,
      '\timport sicherheit',
      `\treverse_proxy ${r.ziel}`,
      '}',
      '',
    )
  }
  zeilen.push(
    '# Alles andere: 404, kein Rückfall auf irgendeinen Kunden (Projektstruktur.md §9.4).',
    ':80 {',
    '\trespond "Unbekannter Host." 404',
    '}',
  )
  return zeilen.join('\n')
}

export function ProxySeite() {
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })

  useEffect(() => {
    let aktuell = true
    Promise.all([ladePartner(), ladePlattform()])
      .then(([partner, plattform]) => {
        if (aktuell) setZustand({ status: 'da', partner, plattform: plattform.plattformDomain })
      })
      .catch((fehler: unknown) => {
        if (aktuell)
          setZustand({
            status: 'fehler',
            meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
          })
      })
    return () => {
      aktuell = false
    }
  }, [])

  return (
    <div>
      <Seitenkopf
        beisatz="Rand (Caddy)"
        titel="Proxy"
        satz="Welche Hosts der Rand kennen muss und wohin sie führen — erzeugt aus den Partnern. Vorschau, noch nicht ausgerollt."
      />
      <div className="flex flex-col gap-6 px-6 pb-8">
        {zustand.status === 'laedt' ? (
          <Laedt>Routen werden erzeugt …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : (
          <ProxyInhalt partner={zustand.partner} plattform={zustand.plattform} />
        )}
      </div>
    </div>
  )
}

function ProxyInhalt({
  partner,
  plattform,
}: {
  partner: VerwaltungPartner[]
  plattform: string | null
}) {
  const routen = routenVon(partner, plattform)
  const caddyfile = caddyfileVon(routen, plattform)
  return (
    <>
      {!plattform ? (
        <Fehler>
          Keine Plattform-Domain gesetzt (PLATTFORM_DOMAIN) — ohne sie gibt es keine Subdomains für
          Partner.
        </Fehler>
      ) : (
        <p className="text-sm text-tertiary">
          Plattform-Domain <b className="font-semibold text-primary">{plattform}</b>. Verwaltung
          unter <span className="tabular-nums">verwaltung.{plattform}</span>.
        </p>
      )}

      <section>
        <h2 className="mb-2 text-[13px] font-semibold text-primary">Hosts</h2>
        {routen.length === 0 ? (
          <Leer titel="Keine Routen">Ohne Partner mit Domain gibt es nichts zu leiten.</Leer>
        ) : (
          <Tabelle kopf={['Host', 'Ziel', 'Zweck', 'Partner', 'Hinweis']}>
            {routen.map((r) => (
              <tr
                key={`${r.partner}:${r.host}:${r.zweck}`}
                className="border-b border-secondary last:border-b-0"
              >
                <td className="py-2 pr-2.5 text-primary tabular-nums">{r.host}</td>
                <td className="py-2 pr-2.5 text-tertiary tabular-nums">{r.ziel}</td>
                <td className="py-2 pr-2.5 text-tertiary">{r.zweck}</td>
                <td className="py-2 pr-2.5 text-tertiary">{r.partner}</td>
                <td className="py-2 text-[12.5px] text-achtung-tief">{r.hinweis ?? ''}</td>
              </tr>
            ))}
          </Tabelle>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-[13px] font-semibold text-primary">Caddyfile (Vorschau)</h2>
        <pre className="overflow-x-auto rounded-xl bg-secondary p-4 text-[12.5px] leading-relaxed text-primary">
          {caddyfile}
        </pre>
      </section>
    </>
  )
}
